import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight } from "lucide-react";
import { Section, SectionTitle, Reveal } from "@/components/Section";
import { useContent } from "@/i18n";
import { Shape3D } from "@/components/Objects3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-transparent border border-line focus:border-cobalt rounded-xl outline-none px-5 py-4 text-sm placeholder:text-cocoa/50 transition-colors duration-300";

export const Contact = () => {
  const { c, ui } = useContent();
  const profile = c.profile;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success(ui.contact.toastSuccess);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(ui.contact.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <Section id="contact" num="11" chapter={ui.contact.chapter}>
      <Shape3D shape="cup" blue size={56} className="absolute right-[6%] top-20 hidden md:block" mouse rotateAmp={16} />
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <SectionTitle>
            {ui.contact.titleA} <span className="italic text-cobalt">{ui.contact.titleB}</span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-cocoa">{ui.contact.intro}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 space-y-6">
              <a href={`mailto:${profile.email}`} data-testid="contact-email-link" className="group flex items-center gap-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-powder group-hover:bg-cobalt transition-colors duration-300">
                  <Mail size={17} className="text-cobalt group-hover:text-cream transition-colors duration-300" />
                </span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-cocoa font-bold">{ui.contact.emailSmall}</span>
                  <span className="link-underline font-bold">{profile.email}</span>
                </span>
              </a>
              <a href={`tel:${profile.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="group flex items-center gap-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-beige group-hover:bg-cobalt transition-colors duration-300">
                  <Phone size={17} className="text-choco group-hover:text-cream transition-colors duration-300" />
                </span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-cocoa font-bold">{ui.contact.phoneLabel}</span>
                  <span className="link-underline font-bold">{profile.phone}</span>
                </span>
              </a>
              <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="group flex items-center gap-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-powder group-hover:bg-cobalt transition-colors duration-300">
                  <Linkedin size={17} className="text-cobalt group-hover:text-cream transition-colors duration-300" />
                </span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-cocoa font-bold">LinkedIn</span>
                  <span className="link-underline font-bold inline-flex items-center gap-1">
                    {ui.contact.connect} <ArrowUpRight size={13} />
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-beige">
                  <MapPin size={17} className="text-choco" />
                </span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-cocoa font-bold">{ui.contact.basedIn}</span>
                  <span className="font-bold">{profile.location}</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={submit} className="bg-white border border-line rounded-2xl -rotate-1 p-8 md:p-10 space-y-6 shadow-[0_30px_60px_-34px_rgba(51,38,25,0.3)]" data-testid="contact-form">
            <p className="font-serif italic text-2xl tracking-tight">{ui.contact.writeToMe}</p>
            <div>
              <label htmlFor="contact-name" className="block text-[10px] tracking-[0.25em] uppercase font-bold text-cocoa mb-2">{ui.contact.nameLabel}</label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={ui.contact.namePh}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-[10px] tracking-[0.25em] uppercase font-bold text-cocoa mb-2">{ui.contact.emailLabel}</label>
              <input
                id="contact-email"
                type="email"
                data-testid="contact-email-input"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={ui.contact.emailPh}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-[10px] tracking-[0.25em] uppercase font-bold text-cocoa mb-2">{ui.contact.messageLabel}</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={ui.contact.messagePh}
                className={`${inputClass} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full rounded-full bg-choco text-cream py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-cobalt disabled:opacity-60 transition-colors duration-400"
            >
              {sending ? ui.contact.sending : ui.contact.send}
            </button>
          </form>
        </Reveal>
      </div>
    </Section>
  );
};
